import {
  fallbackUntilMatch,
  type FRAME_COLORS,
  isPricingProductsEqual,
  MATERIAL_TYPES,
  type PricingProduct,
  TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import {
  MIXED_COLORS,
  MIXED_SIZES,
} from '../../../pages/PhotoStyler/TileDesignerConsts'
import {
  type Discount,
  type DiscountBundle,
  type DiscountBuyOver,
  type DiscountBuyXGetY,
  type DiscountPercent,
  type DiscountsAPI,
  type DiscountTileColor,
  type DiscountTileSize,
  DiscountType,
} from '../types/discount.types'
import { DISCOUNT_TYPES } from 'stores/promoCodeStore/promoCode.consts'
import { extractRawTiers } from 'features/cart/buyoverTiers'
import { logger } from 'services/logger'
import {
  type OfferedDiscount,
  OfferedDiscountSchema,
} from './Discounts.schemas'
import { calculateDiscountPercent } from 'utils/utils'
import {
  type FetchDiscountResponse,
  type FetchPricingItemResponse,
} from '../types/PricingProvider.types'

function formatDiscount({
  tileSize,
  matchingDiscount,
  currency,
  materialType,
  showPromoCodeBadge,
}: {
  tileSize: DiscountTileSize
  matchingDiscount: OfferedDiscount
  currency: string
  materialType: MATERIAL_TYPES
  showPromoCodeBadge: boolean
}): Discount {
  const baseParams = {
    tileSize,
    promoCode: matchingDiscount.promocode || (false as const),
    popular: matchingDiscount.popular || false,
    showPromoCodeBadge,
    isAutomatic: matchingDiscount.isAutomatic || false,
  }

  const metadataBaseParams = {
    discountId: matchingDiscount.discountId,
    materialType,
  }

  switch (matchingDiscount.type) {
    case DiscountType.PERCENT:
      return {
        ...baseParams,
        type: DiscountType.PERCENT,
        discount: {
          type: DiscountType.PERCENT,
          percent_off: matchingDiscount.percentageData.percentOff,
        },
        metadata: {
          ...metadataBaseParams,
          discountType: DiscountType.PERCENT,
          percentOff: matchingDiscount.percentageData.percentOff,
        },
      }
    case DiscountType.BUNDLE:
      return {
        ...baseParams,
        type: DiscountType.BUNDLE,
        metadata: {
          ...metadataBaseParams,
          discountType: DiscountType.BUNDLE,
          bundleTiles: matchingDiscount.bundleData.quantity,
          bundlePrice: matchingDiscount.bundleData.price,
          bundleCurrency: currency,
        },
      }
    case DiscountType.BUYXGETY:
      return {
        ...baseParams,
        type: DiscountType.BUYXGETY,
        metadata: {
          ...metadataBaseParams,
          discountType: DiscountType.BUYXGETY,
          buy: matchingDiscount.buyXGetYData.buy,
          get: matchingDiscount.buyXGetYData.get,
        },
      }
    case DiscountType.BUYOVER:
      return {
        ...baseParams,
        type: DiscountType.BUYOVER,
        metadata: {
          ...metadataBaseParams,
          discountType: DiscountType.BUYOVER,
          buyOverPrice: matchingDiscount.buyOverData.buyOverPrice,
          buyOverDiscount:
            matchingDiscount.buyOverData.buyOverDiscountPercentage,
          bundleCurrency: currency,
          tiers: extractRawTiers(matchingDiscount.buyOverData).map((tier) => ({
            buyOverPrice: tier.buyOverPrice,
            buyOverDiscount: tier.buyOverDiscountPercentage,
          })),
        },
      }
  }
}

const CLASSIC_BUYOVER_DISCOUNT_ID = 'MIXED_SIZES'
const CANVAS_BUYOVER_DISCOUNT_ID = 'CANVAS_MIXED_SIZES'
// const MIXED_COLORS_DISCOUNT_ID = 'MIXED_COLORS'

const MIXED_SIZES_DISCOUNT_IDS = [
  CLASSIC_BUYOVER_DISCOUNT_ID,
  CANVAS_BUYOVER_DISCOUNT_ID,
]

export function isMixedSizesDiscount(discountId: string): boolean {
  return MIXED_SIZES_DISCOUNT_IDS.includes(discountId)
}

export function getDiscountForSize({
  discounts,
  tileSize,
  currency,
  materialType,
  frameColor,
}: {
  discounts: DiscountsAPI
  tileSize: DiscountTileSize
  currency: string
  materialType: MATERIAL_TYPES
  frameColor?: DiscountTileColor
}): Discount | null {
  const mixedSizesDiscountId =
    materialType === MATERIAL_TYPES.CANVAS
      ? CANVAS_BUYOVER_DISCOUNT_ID
      : CLASSIC_BUYOVER_DISCOUNT_ID
  let matchingDiscount

  if (
    tileSize === MIXED_SIZES ||
    // A bit ugly: We want to treat a potential BUNDLE of tiles of the same price as a single color.
    // Since this code is called before the pricing provider, we need to rely on the useTileFrameColor logic to do that.
    (tileSize === TILE_SIZES.SQUARE_8X8 && frameColor === MIXED_COLORS)
  ) {
    matchingDiscount = discounts.find(
      (discount) => discount.discountId === mixedSizesDiscountId
    )
  }

  // Currently we are not really supporting mixed colors discounts
  // if we want to support it we need to add the following code and also adjust the backend
  // if (!matchingDiscount) {
  //   if (frameColor === MIXED_COLORS) {
  //     matchingDiscount = discounts.find(
  //       (discount) => discount.discountId === MIXED_COLORS_DISCOUNT_ID
  //     )
  //   }
  // }

  if (!matchingDiscount) {
    let pricingProduct: PricingProduct = {
      tileSize: tileSize as TILE_SIZES,
      materialType,
      frameColor: frameColor as FRAME_COLORS,
    }

    matchingDiscount = fallbackUntilMatch(
      pricingProduct,
      (pricingProduct: PricingProduct) =>
        discounts.find((discount) =>
          isPricingProductsEqual(discount.pricingProduct, pricingProduct)
        )
    )
  }

  if (!matchingDiscount) {
    return null
  }

  const parsed = OfferedDiscountSchema.safeParse(matchingDiscount)
  if (!parsed.success) {
    logger.warning('Failed to parse discount', {
      discount: matchingDiscount,
      error: parsed.error,
    })
    return null
  }

  const showPromoCodeBadge =
    discounts.filter((discount) => discount.promocode).length === 1 &&
    matchingDiscount.promocode
  return formatDiscount({
    tileSize,
    matchingDiscount: parsed.data,
    currency,
    materialType,
    showPromoCodeBadge,
  })
}

/**
 * Receive a discount and a pricing item, and return how much percentage does
 * the discount represent - for example by checking the price of the bundle
 * compared to the original expected price without discount
 */
export function getDiscountPercentage({
  discount,
  pricingItem,
}: {
  discount: FetchDiscountResponse
  pricingItem: FetchPricingItemResponse
}): number | undefined {
  if (discount.value && pricingItem.value) {
    switch (discount.value.type) {
      case DISCOUNT_TYPES.BUYOVER:
        const { tiers } = (discount.value as DiscountBuyOver).metadata
        // "Save up to X%" surfaces — the ladder is ascending, so the last tier
        // is the largest discount.
        return tiers[tiers.length - 1].buyOverDiscount
      case DISCOUNT_TYPES.BUYXGETY:
        const buyxgetyDiscount = discount.value as DiscountBuyXGetY
        return calculateDiscountPercent(
          pricingItem.value * buyxgetyDiscount.metadata.buy,
          pricingItem.value *
            (buyxgetyDiscount.metadata.buy + buyxgetyDiscount.metadata.get)
        )
      case DISCOUNT_TYPES.PERCENT:
        const percentDiscount = discount.value as DiscountPercent
        return percentDiscount.metadata.percentOff
      case DISCOUNT_TYPES.BUNDLE:
        const bundleDiscount = discount.value as DiscountBundle
        return calculateDiscountPercent(
          bundleDiscount.metadata.bundlePrice,
          pricingItem.value * bundleDiscount.metadata.bundleTiles
        )
    }
  }
}
